import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/atoms/button';
import { CameraAccessDefault } from '@components/atoms/camera-access-default';
import { Typography } from '@components/atoms/typography';
import type { ThemeProps } from '@models/types';
import { useCameraConstraints } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/hooks/use-camera-constraints';
import type { InfoProps } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/selfie/liveness/step/info/types';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import Lottie from 'lottie-react';

import animation from './animation.json';

export const Info = ({ onSubmit }: InfoProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isCameraNotAllowed, setIsCameraNotAllowed] = useState(false);
    const { t } = useTranslation();
    const { cameraConstraints } = useCameraConstraints();
    const theme = useTheme<ThemeProps>();
    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`);

    const handleNextClick = async () => {
        try {
            setIsLoading(true);
            const stream = await navigator.mediaDevices.getUserMedia(cameraConstraints);

            const tracks = stream.getTracks();

            tracks?.forEach((track) => {
                track.stop();
            });
            onSubmit();
        } catch {
            setIsCameraNotAllowed(true);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Stack sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3 }}>
            {isCameraNotAllowed ? (
                <CameraAccessDefault
                    onClose={() => {
                        setIsCameraNotAllowed(false);
                    }}
                />
            ) : (
                <Stack>
                    <Typography
                        tag='h3'
                        variant='h2'
                        sx={{
                            textAlign: 'center',
                            whiteSpace: {
                                sm: 'pre-wrap',
                                xs: 'pre-wrap',
                            },
                        }}
                    >
                        {t('docVerificationWidget.liveness.info.title')}
                    </Typography>
                    <Box
                        sx={{
                            width: {
                                xl: '216px',
                                sm: '108px',
                                xs: '108px',
                            },
                            height: {
                                xl: '208px',
                                sm: '104px',
                                xs: '104px',
                            },
                            mx: 'auto',
                            mt: 4,
                        }}
                    >
                        <Lottie animationData={animation} loop={true} />
                    </Box>
                    <Typography
                        variant='big-text-semibold'
                        sx={{
                            mt: 1,
                            textAlign: 'center',
                            whiteSpace: {
                                sm: 'pre-wrap',
                                xs: 'pre-wrap',
                            },
                        }}
                    >
                        {t('docVerificationWidget.liveness.info.description')}
                    </Typography>
                    <Typography
                        variant='text-medium'
                        sx={{
                            mt: 5,
                            lineHeight: '36px !important',
                            textAlign: 'center',
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {t('docVerificationWidget.liveness.info.requirements')}
                    </Typography>
                </Stack>
            )}
            {!isCameraNotAllowed && (
                <Button
                    variant='primary'
                    sx={{ mt: 4, width: matchesMobileSm ? '100%' : undefined }}
                    size='xl'
                    onClick={handleNextClick}
                    loading={isLoading}
                >
                    {t('common.start')}
                </Button>
            )}
        </Stack>
    );
};
